import { formatMoney, formatPercent, monthName, pluralDays, pluralMonths } from './format.js';

/**
 * The engine emits `key` + structured `data`; the interface renders the sentence. §65
 * Nothing here invents a number — every value comes from the calculation engine. §175
 */
export interface AssumptionPayload {
  key: string;
  data?: Record<string, unknown>;
  text: string;
}

export interface InsightPayload {
  key: string;
  severity: 'info' | 'positive' | 'warning' | 'critical';
  data: Record<string, unknown>;
  fallback: string;
}

type Renderer = (d: Record<string, never>, ctx: { categoryName: (id: string) => string }) => string;

const money = (v: unknown) => formatMoney(Number(v ?? 0));

const TEMPLATES: Record<string, Renderer> = {
  'budget.over': (d) => `חרגת מהתקציב החודשי ב־${money(d.overBy)}.`,
  'budget.projected_over': (d) => `בקצב הנוכחי תחרוג מהתקציב ב־${formatDate(String(d.breachDate))}.`,
  'budget.category_near_limit': (d, ctx) =>
    `ניצלת ${formatPercent(Number(d.usage))} מתקציב ${ctx.categoryName(String(d.categoryId))} — נשארו ${money(d.remaining)}.`,
  'safe_to_spend.negative': (d) =>
    `החיובים הצפויים גבוהים מהיתרה הזמינה ב־${money(d.shortfall)} עד ${formatDate(String(d.nextIncomeDate))}.`,
  'safe_to_spend.per_day': (d) =>
    `אפשר להוציא כ־${money(d.perDay)} ליום במשך ${pluralDays(Number(d.days))} עד ההכנסה הבאה.`,
  'category.up': (d, ctx) =>
    `הוצאת ${formatPercent(Math.abs(Number(d.ratio)))} יותר על ${ctx.categoryName(String(d.categoryId))} מהחודש שעבר (${money(d.delta)}).`,
  'category.down': (d, ctx) =>
    `ההוצאות שלך על ${ctx.categoryName(String(d.categoryId))} נמוכות ב־${formatPercent(Math.abs(Number(d.ratio)))} מהחודש שעבר.`,
  'recurring.price_increase': (d) =>
    `${String(d.merchant)} עלה מ־${money(Math.abs(Number(d.previousAmount)))} ל־${money(Math.abs(Number(d.currentAmount)))}.`,
  'upcoming.next_7_days': (d) =>
    `${money(d.total)} צפויים לרדת ב־7 הימים הקרובים (${Number(d.count)} חיובים).`,
  'savings.rate_up': (d) =>
    `שיעור החיסכון שלך עלה מ־${formatPercent(Number(d.ratePrev))} ל־${formatPercent(Number(d.rateNow))}.`,
  'savings.rate_down': (d) =>
    `שיעור החיסכון שלך ירד מ־${formatPercent(Number(d.ratePrev))} ל־${formatPercent(Number(d.rateNow))}.`,
  'savings.spending_down_savings_flat': () =>
    'ההוצאות שלך ירדו אבל החיסכון לא עלה — משהו חד־פעמי בלע את ההפרש.',
  'emergency_fund.thin': (d) =>
    `כרית הביטחון שלך מכסה פחות מחודש של הוצאות חובה. היעד שלך: ${Number(d.target)} חודשים.`,
  'behaviour.weekend_share': (d) =>
    `${formatPercent(Number(d.share))} מהוצאות הבילוי החודש היו בסוף השבוע.`,
  'goals.contributed': (d) => `העברת ${money(d.contributions)} לחיסכון החודש.`,
  'recurring.monthly_load': (d) =>
    `${Number(d.count)} חיובים קבועים עולים לך בערך ${money(d.monthlyEquivalent)} בחודש — כ־${money(d.annualEquivalent)} בשנה.`,
  'debt.interest_cost': (d) =>
    `נשיאת ${money(d.totalBalance)} בחוב עולה לך כ־${money(d.monthlyInterest)} בריבית החודש.`,
  'income.below_average': (d) =>
    `ההכנסה החודש (${money(d.incomeThisMonth)}) נמוכה ב־${formatPercent(Math.abs(Number(d.ratio)))} מהממוצע האחרון.`,
  'income.above_average': (d) =>
    `ההכנסה החודש (${money(d.incomeThisMonth)}) גבוהה ב־${formatPercent(Math.abs(Number(d.ratio)))} מהממוצע האחרון.`,
  'spending.biggest_purchase': (d) =>
    `ההוצאה הגדולה ביותר החודש: ${money(d.amount)}${d.merchant ? ` ב־${String(d.merchant)}` : ''} — ${formatPercent(Number(d.shareOfSpend))} מכלל ההוצאות.`,
  'spending.future_directed_share': (d) =>
    `${formatPercent(Number(d.futureShare))} מההוצאות החודש (${money(d.amount)}) הופנו לחיסכון או השקעה, לא לצריכה.`,
  'cashflow.trend_improving': () => 'תזרים המזומנים החודשי שלך משתפר שלושה חודשים ברציפות.',
  'cashflow.trend_worsening': () => 'תזרים המזומנים החודשי שלך מחמיר שלושה חודשים ברציפות.',
};

export function renderInsight(
  insight: InsightPayload,
  ctx: { categoryName: (id: string) => string },
): string {
  const template = TEMPLATES[insight.key];
  if (!template) return insight.fallback;
  try {
    return template(insight.data as Record<string, never>, ctx);
  } catch {
    return insight.fallback;
  }
}

/**
 * Assumptions arrive from the engine as key + data + an English sentence. §40
 * The interface renders Hebrew; the English text is the fallback, so a new
 * assumption is never shown as a blank line.
 */
const ASSUMPTIONS: Record<string, (d: Record<string, never>) => string> = {
  'sts.spendable_accounts': (d) =>
    `נספרים רק חשבונות שמסומנים כזמינים להוצאה (${money(d.spendable)} מתוך ${money(d.liquid)} נזילים).`,
  'sts.horizon': (d) => `הטווח: ${pluralDays(Number(d.days))}, עד ${formatDate(String(d.date))}.`,
  'sts.obligations': (d) => `${Number(d.count)} חיובים צפויים לפני ההכנסה הבאה, בסך ${money(d.total)}.`,
  'sts.reserved_savings': (d) =>
    `חיסכון מתוכנן לחודש הזה: ${money(d.reserved)}${Number(d.alreadySaved) > 0 ? ` (כבר הופקדו ${money(d.alreadySaved)})` : ''}.`,
  'sts.no_reserved_savings': () => 'לא נותר חיסכון מתוכנן לשארית החודש.',
  'sts.illiquid_excluded': (d) => `השקעות ונכסים לא נזילים אינם נספרים (${money(d.illiquid)}).`,

  'ef.months_sampled': (d) => `ממוצע הוצאות החובה חושב על ${pluralMonths(Number(d.months))} מלאים.`,
  'ef.essentials_only': () => 'רק קטגוריות תחת "חובה" נספרות בצורך החודשי.',
  'ef.savings_only': () => 'רק חשבונות חיסכון נספרים ככרית ביטחון.',

  'goal.contribution': (d) => `בהנחה של ${money(d.monthly)} הפקדה בכל חודש, החל מהחודש הזה.`,
  'goal.no_contribution': () => 'לא הוגדרה הפקדה חודשית — אין תאריך סיום ליעד.',
  'goal.no_withdrawals': () => 'בהנחה שאין משיכות מהיעד ואין ריבית.',

  'debt.zero_balance': () => 'היתרה כבר אפס.',
  'debt.payment_below_interest': (d) =>
    `תשלום של ${money(d.payment)} אינו מכסה את הריבית החודשית של ${money(d.monthlyInterest)}.`,
  'debt.fixed_apr': (d) => `בהנחת ריבית שנתית קבועה של ${(Number(d.apr) * 100).toFixed(2)}% ובלי חיובים חדשים.`,
  'debt.fixed_apr_monthly': (d) => `בהנחת ריבית שנתית קבועה של ${(Number(d.apr) * 100).toFixed(2)}% בחישוב חודשי.`,
  'debt.fixed_payment': (d) => `בהנחת תשלום של ${money(d.payment)} בכל חודש בלי חיובים חדשים.`,
  'debt.interest_first': () => 'הריבית נזקפת לפני התשלום.',
  'debt.avalanche': () => 'החוב עם הריבית הגבוהה ביותר נפרע ראשון.',
  'debt.snowball': () => 'החוב הקטן ביותר נפרע ראשון.',
  'debt.fixed_terms': () => 'בהנחת ריביות קבועות, בלי חיובים חדשים, וכל מינימום משולם בזמן.',
  'debt.extra_payment': (d) => `בהנחת ${money(d.extra)} נוספים בכל חודש מעבר למינימום.`,
  'debt.minimums_only': () => 'תשלומי מינימום בלבד.',
  'debt.rollover': () => 'חוב שנסגר משחרר את התשלום שלו, שמתגלגל לחוב הבא.',

  'forecast.starting_balance': (d) => `מתחיל מ־${money(d.startingBalance)} מזומן זמין (בלי השקעות).`,
  'forecast.known_payments': (d) => `${Number(d.count)} חיובים ידועים עד ${formatDate(String(d.to))}.`,
  'forecast.daily_rate': (d) =>
    `הוצאות שוטפות בקצב של ${money(d.perDay)} ליום, לפי ${pluralMonths(Number(d.months))} של היסטוריה.`,
  'forecast.recurring_repeat': () => 'בהנחה שחיובים קבועים חוזרים בסכום הרגיל.',
  'forecast.no_surprises': () => 'רכישות חד־פעמיות או חריגות אינן נחזות.',

  'scenario.no_side_effects': () => 'התרחיש רץ על עותק של הנתונים — שום דבר לא נשמר ולא משתנה.',
  'scenario.steady_state': () => 'בהנחה שהכנסות, חיובים קבועים והוצאות שוטפות נשארים כפי שהם היום.',
  'scenario.purchase': (d) => `רכישה חד־פעמית של ${money(d.amount)} מהמזומן הזמין היום.`,
  'scenario.loan': (d) =>
    `הלוואה של ${money(d.principal)} בריבית ${(Number(d.apr) * 100).toFixed(2)}%, בהחזר של ${money(d.monthlyPayment)} לחודש.`,
  'scenario.salary': (d) => `ההכנסה החודשית משתנה ב־${money(d.delta)}, החל מהתשלום הבא.`,
  'scenario.recurring': (d) => `התחייבות קבועה משתנה ב־${money(d.delta)} לחודש.`,
  'scenario.saving': (d) => `${money(d.delta)} נוספים מופרשים בכל חודש.`,
};

export function renderAssumption(assumption: AssumptionPayload): string {
  const template = ASSUMPTIONS[assumption.key];
  if (!template || !assumption.data) return template ? template({} as Record<string, never>) : assumption.text;
  try {
    return template(assumption.data as Record<string, never>);
  } catch {
    return assumption.text;
  }
}

function formatDate(iso: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  return `${Number(iso.slice(8, 10))} ב${monthName(iso)}`;
}

export const t = {
  nav: { home: 'בית', transactions: 'תנועות', plan: 'תכנון', insights: 'ניתוח', me: 'אני' },
  home: {
    greeting: (name: string | null) => (name ? `שלום, ${name}` : 'שלום'),
    total: 'הכסף שלך',
    safeToSpend: 'פנויים להוצאה',
    perDay: (amount: string) => `${amount} ליום`,
    untilIncome: (date: string) => `עד ההכנסה הבאה ב־${date}`,
    upcoming: 'בקרוב',
    thisMonth: 'החודש הזה',
    income: 'הכנסות',
    expenses: 'הוצאות',
    left: 'נשאר',
    saved: 'חיסכון',
    goals: 'יעדים',
    viewAll: 'הצג הכל',
  },
  types: {
    expense: 'הוצאה', income: 'הכנסה', transfer: 'העברה', refund: 'החזר', adjustment: 'תיקון יתרה',
  } as Record<string, string>,
  accountTypes: {
    checking: 'עו״ש', savings: 'חיסכון', cash: 'מזומן', credit_card: 'כרטיס אשראי',
    investment: 'השקעות', liability: 'התחייבות', custom: 'אחר',
  } as Record<string, string>,
  confidence: { high: 'ודאות גבוהה', medium: 'ודאות בינונית', low: 'ודאות נמוכה' } as Record<string, string>,
  common: {
    save: 'שמירה', cancel: 'ביטול', delete: 'מחיקה', edit: 'עריכה', done: 'סיום',
    add: 'הוספה', close: 'סגירה', retry: 'נסה שוב', undo: 'ביטול פעולה',
  },
  errors: {
    save: 'לא הצלחנו לשמור את השינוי. המידע שלך לא אבד. נסה שוב.',
    load: 'לא הצלחנו לטעון את הנתונים. המידע שלך במקום. נסה שוב.',
    offline: 'אין חיבור כרגע. השינוי נשמר מקומית ויסונכרן אוטומטית.',
    ai: 'התובנות החכמות אינן זמינות כרגע.',
  },
};
